import * as React from "react";
import {FunctionComponent, useEffect, useState} from "react";
import {IPlanInfo} from "../../../model/ClientsAndPlans.model";
import PlanApi from "../../../api/PlanApi";
import {setCurrentPlan, setHeaderNotification} from "./HeaderActions";
import {NotificationTypes} from "./HeaderReducer";
import {useDispatch} from "react-redux";
import {ApiError, HttpStatus} from "../../../model/ApiError.model";
import {Tooltip} from "@mui/material";
import TextField from "@mui/material/TextField";
import {AonColors} from "../../../../../css/Colors";
import {AnyThunkDispatch} from "../../../../commonTypes";

export interface IEditPlanNameProps {
    currentPlan: IPlanInfo;
    planList: IPlanInfo[];
}

export const SAVE_FAILED_MESSAGE = "Save failed. Please try again";

export const HeaderEditPlanName: FunctionComponent<IEditPlanNameProps> = (props: IEditPlanNameProps) => {
    const [enableEditing, setEnableEditing] = useState<boolean>(false);
    const [planName, setPlanName] =  useState<string>(props.currentPlan.name);

    const dispatch: AnyThunkDispatch = useDispatch();

    useEffect(() => {
        const selectedPlan = props.planList.find(plan => plan.id === props.currentPlan.id)!;
        if(selectedPlan.name !== props.currentPlan.name) {
            dispatch(setCurrentPlan({...props.currentPlan, name: selectedPlan.name}));
        }
    }, [props.planList]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPlanName(event.target.value);
    };

    const dispatchFailureNotification = (message: string, milliseconds: number = 5000) => {
        dispatch(setHeaderNotification({
            message,
            notificationType: NotificationTypes.FAILURE
        }, milliseconds));
    };

    const enableSaveLink = () => {
        const modifiedPlanName = planName.trim();
        return modifiedPlanName && modifiedPlanName !== props.currentPlan.name;
    };

    const getClassNameForSaveLink = () => {
        return enableSaveLink() ? "" : "disabled";
    };

    const handleSave = () => {
        if (!enableSaveLink()) return;

        PlanApi.updatePlanName(planName)
            .then(() => {
                setEnableEditing(false);
                dispatch(setCurrentPlan({...props.currentPlan, name: planName}));
            })
            .catch((error: ApiError) => {
                if(error.errorNumber === HttpStatus.CONFLICT.valueOf()) {
                    dispatchFailureNotification(error.message!);

                } else if(error.errorNumber === HttpStatus.BAD_REQUEST.valueOf()) {
                    setEnableEditing(false);
                    setPlanName(props.currentPlan.name);
                    dispatchFailureNotification(error.message!, 10000);

                } else {
                    dispatchFailureNotification(SAVE_FAILED_MESSAGE);
                }
            });
    };

    return (
        <div className={"edit-plan-name__container"} data-testid={"edit-plan-name__container"}>
            <Tooltip title={"Edit name"}
                     onClick={() => setEnableEditing(!enableEditing)}
                     data-testid="edit-plan-name__icon">
                <i className="edit-plan-name__icon fa-light fa-pen-to-square"/>
            </Tooltip>
            {enableEditing && <div className="edit-plan-name__popup-container overlay"
                                   data-testid={"edit-plan-name__popup-container"}>
                <div className="edit-plan-name__popup-label" data-testid={"edit-plan-name__popup-label"}>
                    Edit Name
                </div>
                <div data-testid={"edit-plan-name__popup-input"}>
                    <TextField
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                "& fieldset": { borderColor: `${AonColors.AonGray01}` },
                                "&.Mui-focused fieldset": { borderColor: `${AonColors.AonSearchTealDarkHover}`},
                                "&.Mui-error fieldset": { borderColor: `${AonColors.AonErrorDarkRed}`}
                            }
                        }}
                        slotProps={{
                            formHelperText: {
                                style: { marginLeft: "4px" },
                                sx: {
                                    "&.Mui-error": {
                                        color: `${AonColors.AonErrorDarkRed}`,
                                    }
                                }
                            },
                            htmlInput: {
                                style: { color: `${AonColors.AonGray01}`}
                            }
                        }}
                        value={planName}
                        placeholder={"Enter name"}
                        onChange={handleChange}
                        className={"edit-plan-name__popup-input"}
                        helperText={"required"}
                        error={planName.trim() === ""}
                    />
                </div>
                <div className="edit-plan-name__popup-actions">
                    <a
                        data-testid="edit-plan-name__popup-actions-save"
                        className={`edit-plan-name__popup-actions-save ${getClassNameForSaveLink()}`}
                        onClick={() => handleSave()}
                    >
                        Save
                    </a>
                    <a
                        data-testid="edit-plan-name__popup-actions-cancel"
                        className="edit-plan-name__popup-actions-cancel"
                        onClick={() => {
                            setPlanName(props.currentPlan.name);
                            setEnableEditing(false);
                        }}
                    >
                        Cancel
                    </a>
                </div>
            </div>}
        </div>

    );
};